import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  Icon,
  Tooltip,
  Button,
} from '@nextiva/next-ui-components';

import { FeatureContext } from '../../../../components/AccessResolvers/featureContext';

const blockEl = 'pa-acc-info';
const taxIdArticleURL =
  'https://www.nextiva.com/support/articles/10dlc-10-digit-long-code-faqs.html';

const onKeyPressHandler = (e) => {
  const { key } = e;
  const re = /[0-9]/;

  if (!re.test(key)) e.preventDefault();
};

const formatPartialPasswordTaxId = (number) => {
  if (number.length > 4)
    return `\u25CF\u25CF\u25CF\u25CF\u25CF${number.slice(number.length - 4)}`;
  return `\u25CF\u25CF\u25CF\u25CF\u25CF${number}`;
};

const CompanyTaxId = ({ setPristine, payload, setPayload }) => {
  const [showPasswordNormal, setShowPasswordNormal] = useState(false);
  const [showPasswordRe, setShowPasswordRe] = useState(false);
  const [isEditBtn, setIsEditBtn] = useState(false);
  const [errorMessageReEnterTaxId, setErrorMessageReEnterTaxId] = useState('');
  const [taxIdPristine, setTaxIdPristine] = useState(true);
  const [showInfoBar, setShowInfoBar] = useState(true);

  const {
    featureState: { accountInfoTaxIDLink },
  } = useContext(FeatureContext);

  const handleClickShowPassword = (element) => {
    return element
      ? setShowPasswordRe(!showPasswordRe)
      : setShowPasswordNormal(!showPasswordNormal);
  };

  const validateTaxId = () => {
    const { tax_id, tax_re_id } = payload.tax_id_obj;
    const emptyTaxId = !tax_id && !tax_re_id;
    const idsMatch = tax_id === tax_re_id;
    const oneInputWithData =
      (tax_id === '' && tax_re_id !== '') ||
      (tax_id !== '' && tax_re_id === '');
    const oneInputLess9 =
      (tax_id.length < 9 && !tax_re_id.length < 9) ||
      (!tax_id.length < 9 && tax_re_id.length < 9);
    const idsLess9 = oneInputWithData
      ? oneInputLess9 && tax_re_id.length < 9 && tax_id.length < 9
      : tax_id.length === tax_re_id.length
      ? tax_id.length < 9 && idsMatch
      : false;

    if (!taxIdPristine && (emptyTaxId || !idsMatch || idsLess9)) {
      setErrorMessageReEnterTaxId(
        emptyTaxId
          ? `Tax ID/SSN can't be empty.`
          : idsLess9
          ? 'Tax ID/SSN must have 9 digits.'
          : 'Tax ID/SSN does not match.',
      );
    } else {
      setErrorMessageReEnterTaxId('');
    }
  };

  const checkEmptyInputs = () => {
    const { tax_id, tax_re_id } = payload.tax_id_obj;
    return tax_id === '' && tax_re_id === '';
  };

  useEffect(() => {
    validateTaxId();
  }, [payload.tax_id_obj, payload.tax_id_obj.tax_re_id]);

  useEffect(() => {
    if (errorMessageReEnterTaxId) {
      setPristine(true);
    } else {
      setPristine(false);
    }

    if (!isEditBtn) {
      setPristine(true);
    }
  }, [errorMessageReEnterTaxId]);

  const onTaxIdChangeHandler = (e) => {
    if (!isEditBtn) setIsEditBtn(true);
    const { name, value } = e.target;
    setPayload({
      ...payload,
      tax_id_obj: { ...payload.tax_id_obj, [name]: value },
    });
    if (taxIdPristine) {
      setTaxIdPristine(false);
    }
  };

  const handleClose = () => {
    setShowInfoBar(false);
  };

  const buttonClickHandler = () => {
    setTaxIdPristine(true);
    setShowInfoBar(true);
    setPayload({ ...payload, is_cancel_btn_pressed: true });
    setPristine(true);
  };

  return (
    <Grid container spacing={1} alignItems="center">
      {payload.is_cancel_btn_pressed ? (
        <>
          <Grid item>
            <TextField
              placeholder={
                payload.tax_data
                  ? formatPartialPasswordTaxId(payload.tax_data)
                  : 'Enter Tax ID'
              }
              onKeyPress={onKeyPressHandler}
              value={payload.tax_id_obj.tax_id}
              name="tax_id"
              onBlur={validateTaxId}
              onChange={onTaxIdChangeHandler}
              maxLength="9"
              type={showPasswordNormal ? 'text' : 'password'}
              label={
                <>
                  <span className={`${blockEl}__lock-label`}>
                    TAX ID (EIN/SSN)
                  </span>
                  <Tooltip
                    placement="top"
                    title="Enter your EIN (Employment Identification Number) for your company. If your company does not have an EIN enter an SSN (Social Security Number). This data is encrypted for secure storage"
                  >
                    <div className={`${blockEl}__svg-container`}>
                      <Icon icon="help" size={16} />
                    </div>
                  </Tooltip>
                </>
              }
              InputProps={{
                'data-testid':
                  'PlatformAdminUI-AccountInfo-CompanyInfo-EnterTaxId',
                endAdornment:
                  payload.tax_id_obj.tax_id.length === 9 ? (
                    <InputAdornment position="end">
                      <IconButton
                        id="normal"
                        aria-label="toggle password visibility"
                        data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-IconButton"
                        onClick={() => handleClickShowPassword(false)}
                        edge="end"
                      >
                        <Icon
                          icon={showPasswordNormal ? 'hide' : 'watch'}
                          size={16}
                        />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <></>
                  ),
              }}
              data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-EnterTax"
            />
          </Grid>
          <Grid item>
            <TextField
              name="tax_re_id"
              value={payload.tax_id_obj.tax_re_id}
              placeholder={
                payload.tax_data
                  ? formatPartialPasswordTaxId(payload.tax_data)
                  : 'Re-enter Tax ID'
              }
              type={showPasswordRe ? 'text' : 'password'}
              errorMessage={errorMessageReEnterTaxId}
              error={!!errorMessageReEnterTaxId && !taxIdPristine}
              maxLength="9"
              onBlur={validateTaxId}
              onKeyPress={onKeyPressHandler}
              onChange={onTaxIdChangeHandler}
              data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-ValidateTax"
              InputProps={{
                'data-testid':
                  'PlatformAdminUI-AccountInfo-CompanyInfo-ReEnterTaxId',
                endAdornment:
                  payload.tax_id_obj.tax_re_id.length === 9 ? (
                    <InputAdornment position="end">
                      <IconButton
                        id="re-enter"
                        aria-label="toggle password visibility"
                        data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-IconVisibility"
                        onClick={() => handleClickShowPassword(true)}
                        edge="end"
                      >
                        <Icon
                          icon={showPasswordRe ? 'hide' : 'watch'}
                          size={16}
                        />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <></>
                  ),
              }}
            />
          </Grid>
          <Grid item>
            {!checkEmptyInputs() && !errorMessageReEnterTaxId && (
              <Icon
                icon="checkCircleSolid"
                size={20}
                className={`${blockEl}__check-img`}
              />
            )}
          </Grid>
          {showInfoBar && (
            <Alert
              className={`${blockEl}__banner`}
              severity="info"
              onClose={handleClose}
            >
              Tax ID/SSN is required for sending & receiving text messages.{' '}
              {accountInfoTaxIDLink && (
                <a
                  href={taxIdArticleURL}
                  data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-LearnMore"
                >
                  Learn more
                </a>
              )}
            </Alert>
          )}
        </>
      ) : (
        <>
          <Grid item>
            <TextField
              value={formatPartialPasswordTaxId(payload.tax_data)}
              className={`${blockEl}__lock-input`}
              disabled
              label={
                <>
                  <span className={`${blockEl}__lock-label`}>
                    TAX ID (EIN/SSN)
                  </span>
                  <Tooltip
                    placement="top"
                    title="Enter your EIN (Employment Identification Number) for your company. If your company does not have an EIN enter an SSN (Social Security Number). This data is encrypted for secure storage."
                  >
                    <div className={`${blockEl}__svg-container`}>
                      <Icon icon="help" size={16} />
                    </div>
                  </Tooltip>
                </>
              }
              InputProps={{
                'data-testid': 'PlatformAdminUI-AccountInfo-CompanyInfo-TaxId',
                endAdornment: (
                  <InputAdornment
                    position="end"
                    className={`${blockEl}__adornment`}
                  >
                    <Icon icon="lockSolid" size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item className={`${blockEl}__taxId-button`}>
            <Button
              color="primary"
              variant="outlined"
              onClick={buttonClickHandler}
              data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-ChangeButton"
            >
              Change
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default CompanyTaxId;
